import { useState } from 'react';
import { relatoDelPlan } from '../lib/relato.ts';
import { useLoaded } from '../lib/useLoaded.ts';
import { LoadingNotice, EmptyNotice, ErrorNotice } from '../components/StateNotice.tsx';
import { DataTable } from '../components/DataTable.tsx';
import type { DataPort } from '../adapters/port.ts';
import { resolverPlan, generarEntregable, enviarBrief, type PedidoEnviado } from '../adapters/http.ts';

/**
 * Nuevo Brief — con las recomendaciones de la v1.0.
 *
 * 🔑 Cada campo ofrece CUATRO opciones. No son ejemplos decorativos: al tocarlas
 * completan el campo con el texto largo, que es lo que evita que el usuario tenga
 * que redactar desde cero. Es el corazón de la vista y estaba faltando.
 *
 * Los textos son los del export de Martín, no reescritos.
 */

const CAMPANAS = [
  { key: 'global', label: 'Campaña global de marca',
    text: 'Quiero una campaña global de marca para posicionar a Logística TOPS como operador 3PL premium (ANMAT + cargas generales). Necesito una landing, un video institucional y piezas para redes.' },
  { key: 'deposito', label: 'Captar clientes de depósito',
    text: 'Necesito captar empresas que quieran almacenar mercadería. Tengo m² libres en Barracas y el primer mes va bonificado. Quiero una landing, piezas para redes y una secuencia de emails.' },
  { key: 'servicio', label: 'Lanzar un servicio nuevo',
    text: 'Vamos a lanzar un servicio nuevo. Necesito nombre/claim, una landing de lanzamiento, un video corto y piezas para redes y email.' },
  { key: 'promo', label: 'Promo: primer mes bonificado',
    text: 'Promoción: el primer mes de depósito va sin cargo. Quiero comunicar la oferta con una landing, piezas para redes y una secuencia de emails.' },
] as const;

const PUBLICOS = [
  { key: 'lab',    label: 'Laboratorios y farma (ANMAT)',       text: 'Laboratorios, droguerías y productos médicos regulados por ANMAT' },
  { key: 'imp',    label: 'Importadores / e-commerce',          text: 'Importadores y empresas de e-commerce' },
  { key: 'cosm',   label: 'Cosmética, suplementos y sanitarios', text: 'Cosmética, suplementos dietarios y productos sanitarios' },
  { key: 'cargas', label: 'Cargas generales / almacenamiento',  text: 'Empresas con cargas generales que necesitan almacenamiento' },
] as const;

// ⚠️ Cada `text` de acá tiene que ser interpretable por `interpretarPlazo`.
// Un chip que el motor no sabe leer hace que el sistema conteste «no pudimos
// interpretar el plazo» sobre un valor que la propia interfaz sugirió. El test
// D25 lo verifica leyendo este archivo.
const PLAZOS = [
  { key: 'h2',  label: 'En 2 horas',  text: 'en 2 horas' },
  { key: 'h4',  label: 'En 4 horas',  text: 'en 4 horas' },
  { key: 'hoy', label: 'Hoy',         text: 'hoy' },
  { key: 'man', label: 'Mañana',      text: 'mañana' },
  { key: 'vie', label: 'Esta semana', text: 'el viernes' },
] as const;

function Chips({ opciones, elegida, onElegir }: {
  opciones: ReadonlyArray<{ key: string; label: string; text: string }>;
  elegida: string | null;
  onElegir: (key: string, text: string) => void;
}) {
  return (
    <div className="chips">
      {opciones.map((o) => (
        <button
          key={o.key} type="button" className="chip"
          aria-pressed={elegida === o.key}
          onClick={() => onElegir(o.key, o.text)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Briefs({ port }: { port: DataPort }) {
  const b = useLoaded(() => port.briefs());

  const [campana, setCampana] = useState<string | null>(null);
  const [pedido, setPedido] = useState('');
  const [publicoKey, setPublicoKey] = useState<string | null>(null);
  const [publico, setPublico] = useState('');
  const [plazoKey, setPlazoKey] = useState<string | null>(null);
  const [plazo, setPlazo] = useState('');

  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState<PedidoEnviado | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function enviar() {
    setEnviando(true); setError(null);
    const necesidad = CAMPANAS.find((c) => c.key === campana)?.label ?? '';
    const r = await enviarBrief({ title: pedido, need: necesidad, audience: publico, deadline: plazo });
    setEnviando(false);
    if (typeof r === 'string') { setError(r); return; }
    // Se limpia el formulario: dejarlo lleno invita a enviar dos veces lo mismo.
    setEnviado(r);
    setPedido(''); setPublico(''); setPlazo('');
    setCampana(null); setPublicoKey(null); setPlazoKey(null);
  }

  return (
    <section>
      <p className="lede">
        Contanos el objetivo, a quién le hablás y para cuándo lo querés. El sistema define
        el alcance, elige las herramientas mínimas suficientes y devuelve piezas listas para revisar.
      </p>

      <div className="panel">
        <div className="panel__head">
          <span className="eyebrow">Tu pedido</span>
          <span className="panel__rule" />
        </div>

        <div style={{ marginTop: 24 }}>
          <div className="field__row">
            <span className="field__label" id="lbl-campana">¿Qué tipo de campaña?</span>
            <span className="field__help">Elegí una recomendación o escribí la tuya</span>
          </div>
          <Chips
            opciones={CAMPANAS} elegida={campana}
            onElegir={(k, t) => { setCampana(k); setPedido(t); }}
          />
        </div>

        <textarea
          aria-labelledby="lbl-campana"
          value={pedido}
          onChange={(e) => { setPedido(e.target.value); setCampana(null); }}
          placeholder="Ej.: Necesito captar empresas que quieran almacenar mercadería no regulada. Tengo 3.000 m² libres en Barracas y el primer mes va sin cargo. Quiero una landing, un video y piezas para redes."
        />

        <div className="panel__cols">
          <div>
            <label className="field__label" htmlFor="publico">¿Para quién?</label>
            <input
              id="publico" value={publico} placeholder="Ej.: importadores, e-commerce"
              onChange={(e) => { setPublico(e.target.value); setPublicoKey(null); }}
            />
            <Chips
              opciones={PUBLICOS} elegida={publicoKey}
              onElegir={(k, t) => { setPublicoKey(k); setPublico(t); }}
            />
          </div>

          <div>
            <label className="field__label" htmlFor="plazo">¿Para cuándo?</label>
            <input
              id="plazo" value={plazo} placeholder="Ej.: en 2 horas · hoy · mañana · el viernes · 15/08"
              onChange={(e) => { setPlazo(e.target.value); setPlazoKey(null); }}
            />
            <Chips
              opciones={PLAZOS} elegida={plazoKey}
              onElegir={(k, t) => { setPlazoKey(k); setPlazo(t); }}
            />
          </div>
        </div>

        <div className="enviar">
          <button
            type="button" className="btn-primary"
            onClick={() => void enviar()}
            disabled={enviando || pedido.trim().length === 0}
          >
            {enviando ? 'Enviando…' : 'Enviar pedido'}
          </button>

          {enviado && (
            <span className="recibido" role="status">
              <span className="recibido__dot" />
              Pedido {enviado.code} recibido
            </span>
          )}
        </div>

        {enviado && <Acuse p={enviado} />}

        {error && <p className="notice notice--error" role="alert">{error}</p>}

        <p className="note">
          No hace falta que sea preciso. Si falta información, el sistema te la pide antes de
          producir nada.
        </p>
      </div>

      <h2 className="h2">Briefs existentes</h2>
      {b.kind === 'loading' && <LoadingNotice what="los briefs" />}
      {b.kind === 'error' && <ErrorNotice message={b.message} />}
      {b.kind === 'empty' && <EmptyNotice what="briefs" />}
      {b.kind === 'ok' && (
        <DataTable cols={['Código', 'Título', 'Necesidad', 'Estado']}
          rows={b.value.map((x) => [x.code, x.title, x.need, x.status])} />
      )}
    </section>
  );
}

/**
 * El acuse: qué ruta se eligió, por qué, para cuándo, y si se llega al plazo.
 *
 * 🔑 Cuando NO se llega, se dice. Un sistema que sólo comunica buenas noticias
 * deja al usuario descubriendo el problema tarde — que es justo lo que un plazo
 * sirve para evitar.
 */
/**
 * El plan que el sistema propone, y el lugar donde Dirección lo aprueba.
 *
 * 🔑 Está acá, en el mismo acuse del envío, porque la regla dice que la
 * decisión se toma «en el momento que se requiere el trabajo». Los planes que
 * queden sin resolver aparecen además en la sección Aprobaciones.
 *
 * Nada se ejecuta y no hay fecha hasta que se aprueba. Mostrar una fecha antes
 * sería prometerla: quien la lee la recuerda como compromiso.
 */
function Acuse({ p }: { p: PedidoEnviado }) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entrega, setEntrega] = useState<string | null>(null);
  // 'pendiente' → esperando decisión · 'aprobado' · 'rechazado' (final).
  const [fase, setFase] = useState<'pendiente' | 'aprobado' | 'rechazado'>('pendiente');

  // Lo que muestra el acuse. Arranca con el plan del pedido y, si rechazás,
  // pasa a mostrar la contrapropuesta reducida con los botones de nuevo.
  const [vista, setVista] = useState<VistaAcuse | null>(
    p.estado === 'plan_propuesto' && p.plan
      ? {
          entregable: p.plan.entregable, nivel: p.plan.nivel, complejidad: p.plan.complejidad,
          plataformas: p.plan.plataformas, minutos: p.plan.minutos,
          requiereExtension: p.plan.requiereExtension, extraMin: p.plan.extraMin,
          justificacion: p.plan.justificacion, alcance: p.plan.alcance,
          roadmap: p.plan.roadmap.map((t) => ({ label: t.label, min: t.max })),
          reducido: false,
        }
      : null,
  );

  async function decidir(d: 'aprobar' | 'rechazar') {
    if (!p.projectCode || enviando) return;
    setEnviando(true); setError(null);
    const r = await resolverPlan(p.projectCode, d);
    setEnviando(false);
    if (typeof r === 'string') { setError(r); return; }
    if (r.estado === 'contrapropuesta' && r.plan) {
      setVista({
        entregable: r.plan.entregable, nivel: r.plan.nivel, complejidad: r.plan.complejidad,
        plataformas: r.plan.plataformas, minutos: r.plan.minutos,
        requiereExtension: r.plan.requiereExtension, extraMin: r.plan.extraMin,
        justificacion: r.plan.justificacion, alcance: r.plan.alcance,
        roadmap: r.plan.roadmap.map((t) => ({ label: t.label, min: t.max })),
        reducido: true,
      });
      return;
    }
    if (d === 'aprobar' && p.projectCode) void generarEntregable(p.projectCode);
    setFase(d === 'aprobar' ? 'aprobado' : 'rechazado');
    setEntrega(r.hasta);
  }

  const horaART = (iso: string) =>
    new Date(iso).toLocaleString('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hour12: false,
    });

  const mostrarBotones = fase === 'pendiente' && vista !== null;
  const tono = fase === 'aprobado' ? 'ok' : fase === 'rechazado' ? 'warn'
    : vista?.reducido ? 'propuesta' : vista ? 'propuesta' : '';
  const extra = vista ? Math.max(0, vista.minutos - 60) : 0;

  return (
    <div className={'acuse' + (tono ? ` acuse--${tono}` : '')}>
      <div className="acuse__head">
        <span className="eyebrow">
          {fase === 'aprobado' ? 'Plan aprobado · en ejecución'
            : fase === 'rechazado' ? 'Sin acuerdo'
            : vista?.reducido ? 'Contrapropuesta · tu decisión'
            : vista ? 'El sistema armó el plan · necesita tu aprobación'
            : 'El sistema no pudo armar el plan'}
        </span>
        <span className="panel__rule" />
      </div>

      {/* El relato se DERIVA del plan vigente, no del que se propuso al enviar
          el brief. Con el veredicto pegado, la pantalla llegó a narrar el plan
          de 2 horas rechazado al lado del compromiso de 44 minutos aceptado. */}
      <p className="acuse__veredicto">
        {relatoDelPlan(
          fase === 'aprobado' ? 'aprobado' : fase === 'rechazado' ? 'rechazado' : 'pendiente',
          vista ? {
            entregable: vista.entregable, nivel: vista.nivel, minutos: vista.minutos,
            reducido: vista.reducido, pasos: vista.roadmap.length,
          } : null,
          p.veredicto,
        )}
      </p>

      <dl className="acuse__datos">
        <div><dt>Pedido</dt><dd className="mono">{p.code}</dd></div>
        <div><dt>Proyecto</dt><dd className="mono">{p.projectCode ?? '—'}</dd></div>
        <div><dt>Ruta</dt><dd>{p.ruta}</dd></div>
        <div>
          <dt>Entrega</dt>
          <dd>{entrega ? horaART(entrega) : 'sin comprometer — arranca al aprobar'}</dd>
        </div>
        {p.motivo && <div className="acuse__motivo"><dt>Por qué esta ruta</dt><dd>{p.motivo}</dd></div>}
      </dl>

      {vista && (
        <>
          {mostrarBotones && vista.reducido && (
            <p className="acuse__resuelto" role="status">
              Rechazaste el plazo del plan completo. Esta es una versión <strong>reducida</strong>:
              menos entregables y menor complejidad, para que entre en menos tiempo. Aceptala o no.
            </p>
          )}
          <dl className="acuse__pacto">
            <div><dt>Qué recibís</dt><dd><strong>{vista.entregable}</strong> — {vista.nivel}</dd></div>
            <div><dt>Complejidad</dt><dd>{vista.complejidad}</dd></div>
            <div><dt>Plataformas</dt><dd>{vista.plataformas.join(' · ')}</dd></div>
            <div><dt>Duración</dt><dd><strong>{vista.minutos} minutos</strong> desde que aprobás
              {extra > 0 && ` (${extra} min por encima de la hora base)`}</dd></div>
            {vista.reducido && vista.alcance && (
              <div className="acuse__motivo"><dt>Qué se saca respecto del plan completo</dt><dd>{vista.alcance}</dd></div>
            )}
            {!vista.reducido && vista.justificacion && (
              <div className="acuse__motivo"><dt>Por qué pide más de 1 hora</dt><dd>{vista.justificacion}</dd></div>
            )}
          </dl>

          <ol className="acuse__roadmap">
            {vista.roadmap.map((t, i) => (
              <li key={i}><span className="acuse__paso">{t.label}</span><span className="acuse__min">{t.min} min</span></li>
            ))}
          </ol>

          {mostrarBotones && <div className="acuse__decision">
            <button type="button" className="btn-primary" disabled={enviando}
                    onClick={() => void decidir('aprobar')}>
              {enviando ? 'Registrando…' : vista.reducido ? 'Apruebo esta versión — arrancá' : 'Apruebo el plan — arrancá'}
            </button>
            <button type="button" className="btn-ghost" disabled={enviando}
                    onClick={() => void decidir('rechazar')}>
              {vista.reducido ? 'Tampoco — cerralo' : 'No apruebo — proponé menos'}
            </button>
          </div>}
        </>
      )}

      {fase === 'aprobado' && entrega && (
        <p className="acuse__resuelto" role="status">
          En ejecución: <strong>el motor está generando tu entregable</strong>. Aparece en
          Biblioteca en unos minutos. Entrega comprometida: <strong>{horaART(entrega)}</strong>.
        </p>
      )}
      {fase === 'rechazado' && (
        <p className="acuse__resuelto" role="status">
          Tampoco aceptaste la versión reducida, así que <strong>no hay nada comprometido</strong>.
          Cuando quieras lo rearmamos con otro alcance.
        </p>
      )}
      {error && <p className="notice notice--error" role="alert">{error}</p>}
    </div>
  );
}

interface VistaAcuse {
  entregable: string;
  nivel: string;
  complejidad: 'baja' | 'media' | 'alta';
  plataformas: readonly string[];
  minutos: number;
  requiereExtension: boolean;
  extraMin: number;
  justificacion: string | null;
  alcance: string | null;
  roadmap: ReadonlyArray<{ label: string; min: number }>;
  reducido: boolean;
}
